import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sparkles, MapPin, Plus, X, Users, Wallet, CalendarDays, Plane, Download, Send, FileText, Loader2, Clock, Coffee, Sun, Sunset, Utensils, Hotel, Car, MapPinned, CheckCircle, Wand2, Globe, Star, MessageSquare, Bus, FileCheck, Eye, ExternalLink, Video, AlertCircle, Gem } from 'lucide-react';
import { toast } from 'sonner';
import { useCustomerAuth, getCustomerToken } from '../context/CustomerAuthContext';
import GeneratingScreen from '../components/itinerary/GeneratingScreen';
import ItineraryView from '../components/itinerary/ItineraryView';
import { resolveImageUrl } from '../lib/travelApi';
import InlineAuth from '../components/InlineAuth';
import SpinWheelPopup from '../components/SpinWheelPopup';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_PRICE = 150; // Fallback price in INR
const AI_NAME = "NavMind"; // Our AI's name - Nav from Navkar + Mind for intelligence
const GENERATION_TIME_MINUTES = 10;
const TRIP_TYPES = ['Honeymoon', 'Family', 'Adventure', 'Leisure / Relaxation', 'Group / Friends', 'Pilgrimage', 'Luxury', 'Business'];
const TRANSPORT = ['Flight', 'Train', 'Self-drive / Car rental', 'Bus', 'Mix of options'];
const BUDGET_LEVELS = ['Economy', 'Comfort', 'Luxury'];
const HOTEL_PREFS = ['Budget / Hostel', '3-star', '4-star', '5-star', 'Resort / Villa', 'Boutique', 'Any'];

// Popular destinations for autocomplete
const DESTINATIONS = [
  // India
  'Goa, India', 'Kerala, India', 'Rajasthan, India', 'Manali, India', 'Shimla, India', 'Ladakh, India', 
  'Andaman Islands, India', 'Udaipur, India', 'Jaipur, India', 'Agra, India', 'Varanasi, India', 
  'Rishikesh, India', 'Darjeeling, India', 'Ooty, India', 'Munnar, India', 'Coorg, India',
  'Mumbai, India', 'Delhi, India', 'Bangalore, India', 'Chennai, India', 'Kolkata, India', 'Hyderabad, India',
  // Southeast Asia
  'Bali, Indonesia', 'Bangkok, Thailand', 'Phuket, Thailand', 'Krabi, Thailand', 'Chiang Mai, Thailand',
  'Singapore', 'Kuala Lumpur, Malaysia', 'Langkawi, Malaysia', 'Vietnam', 'Hanoi, Vietnam', 
  'Ho Chi Minh City, Vietnam', 'Da Nang, Vietnam', 'Cambodia', 'Siem Reap, Cambodia', 'Philippines',
  'Boracay, Philippines', 'Palawan, Philippines',
  // Middle East
  'Dubai, UAE', 'Abu Dhabi, UAE', 'Maldives', 'Muscat, Oman', 'Jordan', 'Turkey', 'Istanbul, Turkey',
  'Cappadocia, Turkey',
  // Europe
  'Paris, France', 'London, UK', 'Rome, Italy', 'Venice, Italy', 'Florence, Italy', 'Barcelona, Spain',
  'Madrid, Spain', 'Amsterdam, Netherlands', 'Prague, Czech Republic', 'Vienna, Austria', 
  'Zurich, Switzerland', 'Interlaken, Switzerland', 'Santorini, Greece', 'Athens, Greece',
  'Berlin, Germany', 'Munich, Germany', 'Budapest, Hungary', 'Lisbon, Portugal', 'Dublin, Ireland',
  'Edinburgh, Scotland', 'Iceland', 'Norway', 'Sweden', 'Finland', 'Croatia', 'Montenegro',
  // Americas
  'New York, USA', 'Los Angeles, USA', 'Las Vegas, USA', 'San Francisco, USA', 'Miami, USA',
  'Hawaii, USA', 'Mexico', 'Cancun, Mexico', 'Costa Rica', 'Peru', 'Brazil', 'Argentina',
  // Asia Pacific
  'Japan', 'Tokyo, Japan', 'Kyoto, Japan', 'Osaka, Japan', 'South Korea', 'Seoul, South Korea',
  'Hong Kong', 'Taiwan', 'Australia', 'Sydney, Australia', 'Melbourne, Australia', 'New Zealand',
  'Queenstown, New Zealand', 'Fiji',
  // Africa
  'South Africa', 'Cape Town, South Africa', 'Kenya', 'Tanzania', 'Zanzibar', 'Morocco', 'Marrakech, Morocco',
  'Egypt', 'Mauritius', 'Seychelles',
  // Sri Lanka & Nepal
  'Sri Lanka', 'Colombo, Sri Lanka', 'Kandy, Sri Lanka', 'Nepal', 'Kathmandu, Nepal', 'Pokhara, Nepal',
  // Bhutan
  'Bhutan', 'Thimphu, Bhutan', 'Paro, Bhutan',
];

// Common nationalities for visa requirements
const NATIONALITIES = [
  { code: 'IN', name: 'Indian', country: 'India' },
  { code: 'US', name: 'American', country: 'USA' },
  { code: 'GB', name: 'British', country: 'UK' },
  { code: 'AU', name: 'Australian', country: 'Australia' },
  { code: 'CA', name: 'Canadian', country: 'Canada' },
  { code: 'DE', name: 'German', country: 'Germany' },
  { code: 'FR', name: 'French', country: 'France' },
  { code: 'IT', name: 'Italian', country: 'Italy' },
  { code: 'ES', name: 'Spanish', country: 'Spain' },
  { code: 'NL', name: 'Dutch', country: 'Netherlands' },
  { code: 'SG', name: 'Singaporean', country: 'Singapore' },
  { code: 'AE', name: 'Emirati', country: 'UAE' },
  { code: 'JP', name: 'Japanese', country: 'Japan' },
  { code: 'KR', name: 'South Korean', country: 'South Korea' },
  { code: 'CN', name: 'Chinese', country: 'China' },
  { code: 'MY', name: 'Malaysian', country: 'Malaysia' },
  { code: 'TH', name: 'Thai', country: 'Thailand' },
  { code: 'PH', name: 'Filipino', country: 'Philippines' },
  { code: 'ID', name: 'Indonesian', country: 'Indonesia' },
  { code: 'NZ', name: 'New Zealander', country: 'New Zealand' },
  { code: 'ZA', name: 'South African', country: 'South Africa' },
  { code: 'BR', name: 'Brazilian', country: 'Brazil' },
  { code: 'MX', name: 'Mexican', country: 'Mexico' },
  { code: 'RU', name: 'Russian', country: 'Russia' },
  { code: 'PK', name: 'Pakistani', country: 'Pakistan' },
  { code: 'BD', name: 'Bangladeshi', country: 'Bangladesh' },
  { code: 'LK', name: 'Sri Lankan', country: 'Sri Lanka' },
  { code: 'NP', name: 'Nepalese', country: 'Nepal' },
].sort((a, b) => a.name.localeCompare(b.name));

// Known Indian cities (for fallback when user types city without country)
const INDIAN_CITIES = [
  'mumbai', 'delhi', 'bangalore', 'bengaluru', 'chennai', 'kolkata', 'hyderabad', 
  'pune', 'ahmedabad', 'surat', 'jaipur', 'lucknow', 'kanpur', 'nagpur', 'indore',
  'thane', 'bhopal', 'visakhapatnam', 'pimpri-chinchwad', 'patna', 'vadodara',
  'ghaziabad', 'ludhiana', 'agra', 'nashik', 'faridabad', 'meerut', 'rajkot',
  'varanasi', 'srinagar', 'aurangabad', 'dhanbad', 'amritsar', 'allahabad',
  'ranchi', 'howrah', 'coimbatore', 'jabalpur', 'gwalior', 'vijayawada',
  'jodhpur', 'madurai', 'raipur', 'kota', 'chandigarh', 'guwahati', 'solapur',
  'hubli', 'mysore', 'mysuru', 'tiruchirappalli', 'bareilly', 'aligarh', 'tiruppur',
  'gurgaon', 'gurugram', 'noida', 'moradabad', 'jalandhar', 'bhubaneswar', 'salem',
  'warangal', 'guntur', 'bhiwandi', 'saharanpur', 'gorakhpur', 'bikaner', 'amravati',
  'jamshedpur', 'bhilai', 'cuttack', 'firozabad', 'kochi', 'cochin', 'bhavnagar',
  'dehradun', 'durgapur', 'asansol', 'nanded', 'kolhapur', 'ajmer', 'gulbarga',
  'jamnagar', 'ujjain', 'loni', 'siliguri', 'jhansi', 'ulhasnagar', 'nellore',
  'jammu', 'sangli', 'belgaum', 'mangalore', 'ambattur', 'tirunelveli', 'malegaon',
  'gaya', 'jalgaon', 'udaipur', 'maheshtala', 'shimla', 'manali', 'darjeeling',
  'ooty', 'munnar', 'coorg', 'rishikesh', 'haridwar', 'goa', 'kerala', 'rajasthan',
  'ladakh', 'andaman', 'port blair'
];

// Helper to extract country from destination string (e.g., "Paris, France" -> "France")
const extractCountry = (destination) => {
  if (!destination) return '';
  const trimmed = destination.trim();
  const parts = trimmed.split(',').map(p => p.trim());
  
  // If there's a comma, return the last part (country)
  if (parts.length > 1) {
    return parts[parts.length - 1];
  }
  
  // No comma - check if it's a known Indian city
  const lowerDest = trimmed.toLowerCase();
  if (INDIAN_CITIES.some(city => lowerDest.includes(city) || city.includes(lowerDest))) {
    return 'India';
  }
  
  // Check if it matches a known country/single-word destination
  const singleWordCountries = [
    'singapore', 'maldives', 'vietnam', 'cambodia', 'philippines', 'japan', 'turkey',
    'norway', 'sweden', 'finland', 'croatia', 'montenegro', 'mexico', 'peru', 'brazil',
    'argentina', 'egypt', 'mauritius', 'seychelles', 'bhutan', 'nepal', 'iceland'
  ];
  if (singleWordCountries.includes(lowerDest)) {
    return trimmed; // Return as-is (it's a country name)
  }
  
  // Default: assume India for unknown single-word entries (common case for Indian users)
  return 'India';
};

// Helper to check if it's an international trip
const isInternationalTrip = (origin, destinations) => {
  if (!origin || !destinations || destinations.length === 0) return false;
  
  const originCountry = extractCountry(origin).toLowerCase();
  
  // Check if any destination is in a different country
  return destinations.some(dest => {
    const destCountry = extractCountry(dest).toLowerCase();
    // If countries are different and not empty, it's international
    if (!destCountry || destCountry === '') return false;
    return destCountry !== originCountry;
  });
};

// Travel quotes for loading animation
// Default travel quotes (used if no custom quotes in backend)
const DEFAULT_TRAVEL_QUOTES = [
  { quote: "The world is a book and those who do not travel read only one page.", author: "Saint Augustine" },
  { quote: "Travel is the only thing you buy that makes you richer.", author: "Anonymous" },
  { quote: "Life is short and the world is wide.", author: "Simon Raven" },
  { quote: "Adventure is worthwhile in itself.", author: "Amelia Earhart" },
  { quote: "Not all those who wander are lost.", author: "J.R.R. Tolkien" },
  { quote: "Travel far enough, you meet yourself.", author: "David Mitchell" },
  { quote: "To travel is to live.", author: "Hans Christian Andersen" },
  { quote: "Jobs fill your pocket, but adventures fill your soul.", author: "Jaime Lyn Beatty" },
  { quote: "The journey not the arrival matters.", author: "T.S. Eliot" },
  { quote: "Collect moments, not things.", author: "Anonymous" },
  { quote: "Traveling – it leaves you speechless, then turns you into a storyteller.", author: "Ibn Battuta" },
  { quote: "Once a year, go someplace you've never been before.", author: "Dalai Lama" },
  { quote: "The real voyage of discovery consists not in seeking new landscapes, but in having new eyes.", author: "Marcel Proust" },
  { quote: "We travel not to escape life, but for life not to escape us.", author: "Anonymous" },
  { quote: "Travel makes one modest. You see what a tiny place you occupy in the world.", author: "Gustave Flaubert" },
  { quote: "A journey of a thousand miles begins with a single step.", author: "Lao Tzu" },
  { quote: "Take only memories, leave only footprints.", author: "Chief Seattle" },
  { quote: "The gladdest moment in human life is a departure into unknown lands.", author: "Sir Richard Burton" },
  { quote: "Travel is fatal to prejudice, bigotry, and narrow-mindedness.", author: "Mark Twain" },
  { quote: "Blessed are the curious for they shall have adventures.", author: "Lovelle Drachman" },
  { quote: "Travel is never a matter of money but of courage.", author: "Paulo Coelho" },
  { quote: "One's destination is never a place, but a new way of seeing things.", author: "Henry Miller" },
  { quote: "Life begins at the end of your comfort zone.", author: "Neale Donald Walsch" },
  { quote: "The best journeys in life are those that answer questions you never thought to ask.", author: "Rich Ridgeway" },
  { quote: "Fill your life with adventures, not things. Have stories to tell, not stuff to show.", author: "Anonymous" },
  { quote: "Man cannot discover new oceans unless he has the courage to lose sight of the shore.", author: "Andre Gide" },
  { quote: "Wherever you go, go with all your heart.", author: "Confucius" },
  { quote: "Don't tell me how educated you are, tell me how much you have traveled.", author: "Mohammed" },
  { quote: "Travel isn't always pretty. It isn't always comfortable. But the journey changes you.", author: "Anthony Bourdain" },
  { quote: "The world is big and I want to have a good look at it before it gets dark.", author: "John Muir" },
];

const auth = () => ({ Authorization: `Bearer ${getCustomerToken()}` });

// Magic reveal animation shown just before the itinerary appears
const MagicRevealAnimation = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timer1 = setTimeout(() => setPhase(1), 500);
    const timer2 = setTimeout(() => setPhase(2), 1500);
    const timer3 = setTimeout(() => onComplete(), 2500);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
      <div className="text-center">
        {phase === 0 && (
          <div className="animate-bounce">
            <Wand2 className="w-20 h-20 text-amber-500 mx-auto" />
          </div>
        )}
        {phase === 1 && (
          <div className="space-y-4 animate-pulse">
            <div className="flex justify-center gap-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 text-amber-400 animate-spin" fill="currentColor" style={{ animationDuration: `${0.5 + i * 0.2}s` }} />
              ))}
            </div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-orange-500 bg-clip-text text-transparent">
              Your itinerary is ready!
            </h2>
          </div>
        )}
        {phase === 2 && (
          <div className="animate-pulse">
            <div className="w-24 h-24 mx-auto bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-2xl shadow-amber-300">
              <CheckCircle className="w-14 h-14 text-white" />
            </div>
            <p className="mt-4 text-xl text-gray-600">Revealing your adventure...</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Countdown Timer Component for generation stage - positioned below quotes
const SampleItineraryModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  // Sample Bali itinerary data - showcasing all features
  const sampleItinerary = {
    title: "Magical Bali Honeymoon Adventure",
    cover_image: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1200&q=80",
    overview: "Discover the enchanting island of Bali with this perfectly curated 5-day honeymoon itinerary. From stunning temples and terraced rice paddies to pristine beaches and world-class dining, experience romance at every turn.",
    travellers: "2 adults",
    best_time: "April-October (dry season)",
    days: [
      {
        day: 1,
        date: "Day 1",
        title: "Arrival & Seminyak Beach Sunset",
        location: "Seminyak, Bali",
        image: "https://images.unsplash.com/photo-1573790387438-4da905039392?w=800&q=80",
        morning: {
          time: "Morning",
          activities: [
            { name: "Ngurah Rai Airport Arrival", description: "Private airport transfer to hotel with flower garland welcome", duration: "1.5 hours", entry_fee: "Included", maps_link: "https://www.google.com/maps/search/Ngurah+Rai+International+Airport+Bali" },
          ]
        },
        afternoon: {
          time: "Afternoon",
          activities: [
            { name: "Hotel Check-in & Spa", description: "Relax with a couple's Balinese massage", duration: "2 hours", entry_fee: "₹3,500/couple", maps_link: "https://www.google.com/maps/search/W+Bali+Seminyak+Spa" },
          ]
        },
        evening: {
          time: "Evening",
          activities: [
            { name: "Seminyak Beach Sunset", description: "Watch the famous Bali sunset with cocktails at a beach club", duration: "2 hours", entry_fee: "Free (drinks extra)", maps_link: "https://www.google.com/maps/search/Seminyak+Beach+Bali" },
            { name: "Dinner at Ku De Ta", description: "Fine dining with ocean views", duration: "2 hours", entry_fee: "₹4,000/person", maps_link: "https://www.google.com/maps/search/Ku+De+Ta+Seminyak+Bali" },
          ]
        },
        meals: {
          breakfast: { restaurant: "Hotel", cuisine: "International Buffet", cost_per_person: "Included", maps_link: "" },
          lunch: { restaurant: "Sisterfields Cafe", cuisine: "Healthy Brunch", cost_per_person: "₹1,200", maps_link: "https://www.google.com/maps/search/Sisterfields+Cafe+Seminyak" },
          dinner: { restaurant: "Ku De Ta", cuisine: "Asian Fusion", cost_per_person: "₹4,000", maps_link: "https://www.google.com/maps/search/Ku+De+Ta+Seminyak+Bali" },
        },
        stay: { hotel: "W Bali - Seminyak", rating: "5/5", price_per_night: "₹18,500", why: "Iconic beachfront property with stunning pool villas", maps_link: "https://www.google.com/maps/search/W+Bali+Seminyak" },
        day_cost_inr: "₹26,000-30,000"
      },
      {
        day: 2,
        date: "Day 2",
        title: "Ubud Art & Culture Immersion",
        location: "Ubud, Bali",
        image: "https://images.unsplash.com/photo-1555400038-63f5ba517a47?w=800&q=80",
        morning: {
          time: "Morning",
          activities: [
            { name: "Tegallalang Rice Terraces", description: "Instagram-famous rice terraces with jungle swing", duration: "2 hours", entry_fee: "₹350", maps_link: "https://www.google.com/maps/search/Tegallalang+Rice+Terrace+Ubud" },
            { name: "Tirta Empul Temple", description: "Sacred water temple for purification ritual", duration: "1.5 hours", entry_fee: "₹250", maps_link: "https://www.google.com/maps/search/Tirta+Empul+Temple+Bali" },
          ]
        },
        afternoon: {
          time: "Afternoon",
          activities: [
            { name: "Ubud Monkey Forest", description: "Walk through sacred sanctuary with playful macaques", duration: "1.5 hours", entry_fee: "₹800", maps_link: "https://www.google.com/maps/search/Sacred+Monkey+Forest+Ubud" },
            { name: "Ubud Art Market", description: "Shop for handcrafted souvenirs and batik", duration: "1 hour", entry_fee: "Free", maps_link: "https://www.google.com/maps/search/Ubud+Art+Market" },
          ]
        },
        evening: {
          time: "Evening",
          activities: [
            { name: "Traditional Kecak Fire Dance", description: "Mesmerizing sunset performance at Uluwatu", duration: "1 hour", entry_fee: "₹1,200", maps_link: "https://www.google.com/maps/search/Kecak+Dance+Uluwatu+Temple" },
          ]
        },
        meals: {
          breakfast: { restaurant: "Hotel", cuisine: "International", cost_per_person: "Included", maps_link: "" },
          lunch: { restaurant: "Locavore", cuisine: "Modern Indonesian", cost_per_person: "₹2,500", maps_link: "https://www.google.com/maps/search/Locavore+Restaurant+Ubud" },
          dinner: { restaurant: "Swept Away at Samaya", cuisine: "Riverside Fine Dining", cost_per_person: "₹3,500", maps_link: "https://www.google.com/maps/search/Swept+Away+Samaya+Ubud" },
        },
        stay: { hotel: "The Samaya Ubud", rating: "5/5", price_per_night: "₹22,000", why: "Luxury riverside villas surrounded by rice paddies", maps_link: "https://www.google.com/maps/search/The+Samaya+Ubud+Bali" },
        day_cost_inr: "₹32,000-38,000"
      }
    ],
    flights: [
      {
        route: "Mumbai to Bali (Denpasar)",
        options: [
          { airline: "Singapore Airlines", flight_no: "SQ-423", departure: "08:15", arrival: "18:30", duration: "9h 15m", stops: "1 stop (SIN)", approx_price_inr: "32,000-45,000" },
          { airline: "IndiGo", flight_no: "6E-1055", departure: "06:00", arrival: "17:45", duration: "10h 45m", stops: "1 stop (KUL)", approx_price_inr: "22,000-28,000" },
        ]
      }
    ],
    hotels: [
      { name: "W Bali - Seminyak", category: "5-star", location: "Seminyak Beach", price_per_night_inr: "₹18,500", rating: "4.8/5", amenities: ["Beach Access", "Infinity Pool", "Spa", "Multiple Restaurants"], booking_tip: "Book 3 months ahead for best rates", why: "Iconic beachfront with vibrant nightlife nearby", maps_link: "https://www.google.com/maps/search/W+Bali+Seminyak", image: "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=400&q=80" },
      { name: "The Samaya Ubud", category: "5-star", location: "Sayan Valley, Ubud", price_per_night_inr: "₹22,000", rating: "4.9/5", amenities: ["River View", "Private Villas", "Yoga Pavilion", "Spa"], booking_tip: "Request villa with rice paddy view", why: "Ultimate tranquility surrounded by nature", maps_link: "https://www.google.com/maps/search/The+Samaya+Ubud+Bali", image: "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=400&q=80" },
      { name: "Viceroy Bali", category: "5-star", location: "Ubud Valley", price_per_night_inr: "₹35,000", rating: "4.9/5", amenities: ["Valley View", "Infinity Pool", "Fine Dining"], booking_tip: "Perfect for special occasions", why: "Award-winning luxury with stunning views", maps_link: "https://www.google.com/maps/search/Viceroy+Bali+Ubud", image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
    ],
    visa_requirements: {
      nationality: "Indian",
      destination: "Indonesia (Bali)",
      visa_required: true,
      visa_type: "Visa on Arrival (VOA)",
      validity: "30 days (extendable once)",
      cost: "IDR 500,000 (~₹2,700)",
      processing: "On arrival at airport",
      documents: ["Passport valid 6+ months", "Return ticket", "Hotel booking proof", "Sufficient funds proof"],
      important_note: "VOA can be extended once for another 30 days at immigration office"
    },
    important_contacts: [
      { type: "Emergency", number: "112", description: "Indonesia Emergency Services" },
      { type: "Indian Embassy", number: "+62 21 5224150", description: "Embassy of India, Jakarta" },
      { type: "Tourist Police", number: "+62 361 754599", description: "Bali Tourist Police" },
      { type: "Medical", number: "+62 361 761263", description: "BIMC Hospital Kuta (24/7)" },
    ],
    packing_suggestions: [
      "Light cotton clothes & swimwear",
      "Sarong for temple visits",
      "Reef-safe sunscreen",
      "Mosquito repellent",
      "Comfortable walking shoes",
      "Waterproof phone pouch"
    ],
    tips: [
      "💡 Best exchange rate: Get IDR at authorized money changers in Kuta/Seminyak",
      "🛕 Temple dress code: Carry a sarong for temple visits (or rent at entrance)",
      "🏍️ Book Grab/Gojek app for affordable transport",
      "💰 Bargain at markets - start at 50% of asking price",
      "📱 Get local SIM at airport for data (Telkomsel recommended)",
      "🌧️ Pack a light rain jacket for unexpected showers",
      "💳 Many places prefer cash; ATMs are widely available"
    ]
  };

  // Helper to render activity with Google Maps link
  const renderActivity = (act) => (
    <div key={act.name} className="bg-white/70 rounded-lg p-3 mb-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          {act.maps_link ? (
            <a href={act.maps_link} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline inline-flex items-center gap-1">
              {act.name} <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            <span className="font-medium text-gray-900">{act.name}</span>
          )}
          {act.description && <p className="text-sm text-gray-600 mt-0.5">{act.description}</p>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-2 text-xs">
        {act.duration && <span className="bg-gray-100 px-2 py-0.5 rounded">⏱ {act.duration}</span>}
        {act.entry_fee && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">💰 {act.entry_fee}</span>}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        
        <div 
          className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with Cover Image */}
          <div className="relative">
            <img 
              src={sampleItinerary.cover_image} 
              alt="Bali" 
              className="w-full h-48 md:h-64 object-cover rounded-t-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent rounded-t-2xl" />
            <button onClick={onClose} className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 z-10">
              <X className="w-5 h-5 text-white" />
            </button>
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="flex items-center gap-2 text-amber-300 text-xs mb-2">
                <Eye className="w-4 h-4" />
                <span className="bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full">Sample Preview</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">{sampleItinerary.title}</h2>
              <div className="flex flex-wrap gap-4 text-sm text-white/90">
                <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {sampleItinerary.travellers}</span>
                <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" /> 5 Days</span>
                <span className="flex items-center gap-1"><MapPinned className="w-4 h-4" /> Best: {sampleItinerary.best_time}</span>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Overview */}
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-4">
              <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" /> Trip Overview
              </h3>
              <p className="text-gray-700">{sampleItinerary.overview}</p>
            </div>

            {/* Visa Requirements */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-blue-600" /> Visa Requirements
                <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Visa on Arrival</span>
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600"><strong>For:</strong> {sampleItinerary.visa_requirements.nationality} travelers</p>
                  <p className="text-gray-600"><strong>Type:</strong> {sampleItinerary.visa_requirements.visa_type}</p>
                  <p className="text-gray-600"><strong>Validity:</strong> {sampleItinerary.visa_requirements.validity}</p>
                  <p className="text-gray-600"><strong>Cost:</strong> {sampleItinerary.visa_requirements.cost}</p>
                </div>
                <div>
                  <p className="text-gray-700 font-medium mb-1">Required Documents:</p>
                  <ul className="text-gray-600 text-xs space-y-1">
                    {sampleItinerary.visa_requirements.documents.slice(0, 3).map((doc, i) => (
                      <li key={i} className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-500" /> {doc}
                      </li>
                    ))}
                    <li className="text-blue-600 font-medium">+ more details in full itinerary...</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Flight Options */}
            <div className="bg-sky-50 rounded-xl p-4">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Plane className="w-5 h-5 text-sky-600" /> Flight Options
              </h3>
              {sampleItinerary.flights.map((flight, i) => (
                <div key={i}>
                  <p className="text-sm font-medium text-gray-700 mb-2">{flight.route}</p>
                  <div className="grid gap-2">
                    {flight.options.map((opt, j) => (
                      <div key={j} className="bg-white rounded-lg p-3 flex flex-wrap items-center gap-3 text-sm shadow-sm">
                        <span className="font-medium text-sky-600">{opt.airline}</span>
                        <span className="text-gray-500">{opt.departure} → {opt.arrival}</span>
                        <span className="text-gray-400 text-xs">{opt.stops}</span>
                        <span className="text-amber-600 font-semibold ml-auto">₹{opt.approx_price_inr}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-sky-600 mt-2 font-medium">+ Return flight options in full itinerary...</p>
                </div>
              ))}
            </div>

            {/* Hotels Section - Enhanced */}
            <div className="bg-gray-900 text-white rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-700">
                <h3 className="font-bold flex items-center gap-2">
                  <Hotel className="w-5 h-5 text-amber-400" /> Recommended Hotels
                </h3>
              </div>
              <div className="p-4 space-y-4">
                {sampleItinerary.hotels.map((h, i) => (
                  <div key={i} className="bg-gray-800 rounded-xl overflow-hidden">
                    <div className="flex flex-col md:flex-row">
                      <img src={h.image} alt={h.name} className="w-full md:w-40 h-32 object-cover" />
                      <div className="p-4 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            {h.maps_link ? (
                              <a href={h.maps_link} target="_blank" rel="noopener noreferrer" className="font-semibold text-amber-300 hover:underline inline-flex items-center gap-1">
                                {h.name} <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <p className="font-semibold text-white">{h.name}</p>
                            )}
                            <p className="text-gray-400 text-sm">{h.location}</p>
                          </div>
                          <span className="bg-amber-500/20 text-amber-300 text-xs px-2 py-1 rounded">{h.category}</span>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-amber-400 font-bold">{h.price_per_night_inr}/night</span>
                          <span className="text-gray-400">⭐ {h.rating}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {h.amenities.slice(0, 3).map((a, j) => (
                            <span key={j} className="bg-gray-700 text-gray-300 px-2 py-0.5 rounded text-xs">{a}</span>
                          ))}
                          {h.amenities.length > 3 && <span className="text-gray-500 text-xs">+{h.amenities.length - 3} more</span>}
                        </div>
                        {h.booking_tip && <p className="text-xs text-amber-400 mt-2">💡 {h.booking_tip}</p>}
                      </div>
                    </div>
                  </div>
                ))}
                <p className="text-center text-gray-400 text-sm">+ Alternative budget & luxury options in full itinerary...</p>
              </div>
            </div>

            {/* Day by Day - Show first 2 days as preview */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CalendarDays className="w-6 h-6 text-amber-600" /> Day-by-Day Itinerary
              </h3>
              
              {sampleItinerary.days.map((day, i) => (
                <div key={i} className="border border-gray-200 rounded-xl mb-4 overflow-hidden">
                  {/* Day Header with Image */}
                  <div className="relative">
                    <img src={day.image} alt={day.title} className="w-full h-36 object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <div className="flex items-center gap-3">
                        <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">Day {day.day}</span>
                        <div>
                          <h4 className="font-bold">{day.title}</h4>
                          <span className="text-white/80 text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> {day.location}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 space-y-3">
                    {/* Morning */}
                    <div className="bg-yellow-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-yellow-700 font-medium text-sm mb-2">
                        <Sun className="w-4 h-4" /> Morning
                      </div>
                      {day.morning.activities.map(renderActivity)}
                    </div>
                    
                    {/* Afternoon */}
                    <div className="bg-orange-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-orange-700 font-medium text-sm mb-2">
                        <Coffee className="w-4 h-4" /> Afternoon
                      </div>
                      {day.afternoon.activities.map(renderActivity)}
                    </div>
                    
                    {/* Evening */}
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-purple-700 font-medium text-sm mb-2">
                        <Sunset className="w-4 h-4" /> Evening
                      </div>
                      {day.evening.activities.map(renderActivity)}
                    </div>

                    {/* Stay info */}
                    <div className="bg-gray-50 rounded-lg p-3 flex items-start gap-3">
                      <Hotel className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <span className="text-xs text-gray-500 font-medium">STAY</span>
                        {day.stay.maps_link ? (
                          <a href={day.stay.maps_link} target="_blank" rel="noopener noreferrer" className="block font-medium text-blue-600 hover:underline">
                            {day.stay.hotel} <ExternalLink className="w-3 h-3 inline" />
                          </a>
                        ) : (
                          <p className="font-medium text-gray-900">{day.stay.hotel}</p>
                        )}
                        <p className="text-sm text-amber-600 font-semibold">{day.stay.price_per_night}/night • ⭐ {day.stay.rating}</p>
                      </div>
                    </div>

                    {/* Day cost */}
                    <div className="text-right">
                      <span className="inline-block bg-amber-100 text-amber-700 font-bold text-sm px-3 py-1 rounded-full">
                        💰 Est. {day.day_cost_inr}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Remaining days indicator */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-dashed border-amber-300 rounded-xl p-5 text-center">
                <p className="text-gray-700 font-semibold text-lg">
                  + 3 more detailed days in your full itinerary
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  <span className="font-medium">Day 3:</span> Mount Batur Sunrise Trek & Waterfalls • 
                  <span className="font-medium"> Day 4:</span> Uluwatu Temple & Beach Clubs • 
                  <span className="font-medium"> Day 5:</span> Shopping & Departure
                </p>
                <p className="text-amber-600 text-sm mt-2 font-medium">Each day with full activities, meals, costs & Google Maps links!</p>
              </div>
            </div>

            {/* Important Information */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-red-500" /> Important Contacts & Info
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {sampleItinerary.important_contacts.slice(0, 2).map((contact, i) => (
                  <div key={i} className="bg-white rounded-lg p-3 text-sm">
                    <p className="font-medium text-gray-900">{contact.type}</p>
                    <p className="text-blue-600 font-mono">{contact.number}</p>
                    <p className="text-gray-500 text-xs">{contact.description}</p>
                  </div>
                ))}
              </div>
              <p className="text-red-600 text-sm mt-3 font-medium">+ Embassy details, hospital contacts & more in full itinerary...</p>
            </div>

            {/* Total Cost */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-xl p-5 text-center">
              <p className="text-gray-400 text-sm mb-2">Estimated Total Cost (2 persons, 5 days)</p>
              <p className="text-4xl font-bold text-amber-400">₹1,88,500</p>
              <div className="flex flex-wrap justify-center gap-3 mt-3 text-xs text-gray-400">
                <span>✈️ Flights ₹50,000</span>
                <span>🏨 Hotels ₹75,500</span>
                <span>🎯 Activities ₹25,000</span>
                <span>🍽️ Food ₹30,000</span>
                <span>🚗 Transport ₹8,000</span>
              </div>
            </div>

            {/* Packing Suggestions */}
            <div className="bg-green-50 rounded-xl p-4">
              <h3 className="font-bold text-gray-900 mb-3">🧳 Packing Suggestions</h3>
              <div className="flex flex-wrap gap-2">
                {sampleItinerary.packing_suggestions.slice(0, 4).map((item, i) => (
                  <span key={i} className="bg-white text-gray-700 text-sm px-3 py-1 rounded-full border border-green-200">{item}</span>
                ))}
                <span className="text-green-600 text-sm font-medium">+ {sampleItinerary.packing_suggestions.length - 4} more items in full itinerary...</span>
              </div>
            </div>

            {/* Tips Preview */}
            <div className="bg-amber-50 rounded-xl p-4">
              <h3 className="font-bold text-gray-900 mb-3">💡 Travel Tips</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                {sampleItinerary.tips.slice(0, 4).map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
                <li className="text-amber-600 font-medium">+ {sampleItinerary.tips.length - 4} more insider tips in full itinerary...</li>
              </ul>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-center text-white">
              <h3 className="text-2xl font-bold mb-2">Ready to create YOUR dream itinerary?</h3>
              <p className="text-white/90 mb-4">Get a personalized itinerary like this for any destination in just 10 minutes!</p>
              <Button 
                onClick={onClose}
                className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-3 text-lg font-bold shadow-lg"
                data-testid="create-my-itinerary-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" /> Create My Itinerary Now
              </Button>
              <p className="text-white/70 text-sm mt-3">✨ All places linked to Google Maps • 📄 Download as PDF</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Destination Autocomplete Component
const DestinationAutocomplete = ({ value, onChange, onSelect, placeholder, keepValueOnSelect = false }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    if (value.length >= 2) {
      const filtered = DESTINATIONS.filter(dest => 
        dest.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 8);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setHighlightedIndex(-1);
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        onSelect(suggestions[highlightedIndex]);
        setShowSuggestions(false);
        if (!keepValueOnSelect) onChange('');
      } else if (value.trim()) {
        onSelect(value.trim());
        if (!keepValueOnSelect) onChange('');
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSelect = (dest) => {
    onSelect(dest);
    setShowSuggestions(false);
    if (!keepValueOnSelect) onChange('');
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => value.length >= 2 && suggestions.length > 0 && setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder={placeholder}
        data-testid="destination-input"
      />
      {showSuggestions && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((dest, index) => (
            <button
              key={dest}
              type="button"
              className={`w-full px-4 py-2 text-left text-sm hover:bg-amber-50 flex items-center gap-2 ${
                index === highlightedIndex ? 'bg-amber-100' : ''
              }`}
              onMouseDown={() => handleSelect(dest)}
            >
              <MapPin className="w-4 h-4 text-amber-500 shrink-0" />
              <span>{dest}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const InstantItineraryPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { customer, loading } = useCustomerAuth();
  const [params, setParams] = useSearchParams();
  const orderId = params.get('order_id');

  const [form, setForm] = useState({ 
    origin: '', 
    destinations: [], 
    trip_type: TRIP_TYPES[0], 
    transport: TRANSPORT[0], 
    hotel_preference: ['4-star'], 
    adults: 2, 
    kids: 0, 
    budgetLevel: 'Comfort', 
    budgetAmount: '', 
    days: 4,
    startDate: '',
    specialNotes: '',
    // Pre-booked flight details (optional) — helps AI plan realistic arrival & recovery days
    flightBooked: false,
    outboundFlightNo: '',
    outboundDeparture: '',   // datetime-local: departure from origin
    outboundArrival: '',     // datetime-local: arrival at destination
    returnFlightNo: '',
    returnDeparture: '',     // datetime-local: departure from destination
    returnArrival: '',       // datetime-local: arrival back home
  });
  const [destInput, setDestInput] = useState('');
  const [originInput, setOriginInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState('form');
  const [itinerary, setItinerary] = useState(null);
  const [pastItineraries, setPastItineraries] = useState([]);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showAuth, setShowAuth] = useState(false);
  const pollRef = useRef(null);
  const pollCount = useRef(0);
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidation, setDiscountValidation] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  
  // Spin wheel state
  const [showSpinWheel, setShowSpinWheel] = useState(false);
  const spinWheelShownRef = useRef(false);
  
  // Sample itinerary preview modal state
  const [showSamplePreview, setShowSamplePreview] = useState(false);

  // NavMind intro video state
  // Hindi video shows by default for these language selections
  const HINDI_DEFAULT_LANGS = React.useMemo(() => new Set(['hi', 'pa', 'gu', 'mr', 'bn']), []);
  const [videoLang, setVideoLang] = useState(() => (HINDI_DEFAULT_LANGS.has(i18n.language) ? 'hi' : 'en'));
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoManuallySet, setVideoManuallySet] = useState(false);
  const initialPageLangRef = useRef(i18n.language);
  // Wrap manual clicks so we know the visitor chose it
  const setVideoLangManually = useCallback((code) => {
    setVideoManuallySet(true);
    setVideoLang(code);
    setVideoPlaying(false);
  }, []);
  
  // Traveler nationalities state (for visa requirements)
  const [travelerNationalities, setTravelerNationalities] = useState([]);
  
  // Countdown timer state
  const [generationStartTime, setGenerationStartTime] = useState(null);
  const [genDestinations, setGenDestinations] = useState([]);
  const generationStartTimeRef = useRef(null); // Use ref to track if start time has been set
  
  // Travel quotes state (fetched from backend)
  const [travelQuotes, setTravelQuotes] = useState([]);
  
  // Hotel recommendation state
  const [wantHotelRecommendations, setWantHotelRecommendations] = useState(true);
  const [customHotels, setCustomHotels] = useState([]); // Array of {name, checkIn, checkOut}
  const [newHotel, setNewHotel] = useState({ name: '', checkIn: '', checkOut: '' });
  
  // Geo-pricing state
  const [pricing, setPricing] = useState({
    price: DEFAULT_PRICE,
    symbol: '₹',
    currency: 'INR',
    country: 'India',
    country_code: 'IN',
    formatted: '₹150'
  });
  const [pricingLoaded, setPricingLoaded] = useState(false);

  // NavMind intro video language default:
  //  - Geo-detected (same IP lookup that drives pricing): visitors from India get the Hindi video
  //  - If the visitor explicitly switches the page language, that wins over geo
  //  - If they tap EN/HI on the video toggle, that wins over everything
  useEffect(() => {
    if (videoManuallySet) return;
    const pageLangChanged = i18n.language !== initialPageLangRef.current;
    const next = pageLangChanged
      ? (HINDI_DEFAULT_LANGS.has(i18n.language) ? 'hi' : 'en')
      : ((HINDI_DEFAULT_LANGS.has(i18n.language) || pricing.country_code === 'IN') ? 'hi' : 'en');
    setVideoLang(prev => (prev === next ? prev : next));
    setVideoPlaying(false);
  }, [i18n.language, videoManuallySet, HINDI_DEFAULT_LANGS, pricing.country_code]);

  
  // Invoice state
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [emailingInvoice, setEmailingInvoice] = useState(false);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Hotel preference is multi-select; "Any" is exclusive and never empty
  const toggleHotelPref = (pref) => setForm((p) => {
    const current = p.hotel_preference || [];
    if (pref === 'Any') return { ...p, hotel_preference: ['Any'] };
    const next = current.includes(pref)
      ? current.filter((x) => x !== pref)
      : [...current.filter((x) => x !== 'Any'), pref];
    return { ...p, hotel_preference: next.length ? next : ['Any'] };
  });
  const addDest = (dest) => { 
    const v = dest || destInput.trim(); 
    if (v && !form.destinations.includes(v)) { 
      setField('destinations', [...form.destinations, v]); 
      setDestInput(''); 
    } 
  };
  const removeDest = (i) => setField('destinations', form.destinations.filter((_, idx) => idx !== i));
  const selectOrigin = (origin) => {
    setField('origin', origin);
    setOriginInput(origin);
  };
  
  // Custom hotel management
  const addCustomHotel = () => {
    if (newHotel.name.trim()) {
      setCustomHotels([...customHotels, { ...newHotel, name: newHotel.name.trim() }]);
      setNewHotel({ name: '', checkIn: '', checkOut: '' });
    }
  };
  const removeCustomHotel = (idx) => {
    setCustomHotels(customHotels.filter((_, i) => i !== idx));
  };

  const loadPast = useCallback(async () => {
    if (!getCustomerToken()) return;
    try {
      const res = await fetch(`${API_URL}/api/my/itineraries`, { headers: auth() });
      if (res.ok) setPastItineraries(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (customer) loadPast(); }, [customer, loadPast]);

  // Auto-update traveler nationalities array when number of travelers changes
  useEffect(() => {
    const totalTravelers = Number(form.adults || 1) + Number(form.kids || 0);
    setTravelerNationalities(prev => {
      // If we need more slots, add empty ones
      if (prev.length < totalTravelers) {
        return [...prev, ...Array(totalTravelers - prev.length).fill('')];
      }
      // If we need fewer slots, trim the array
      if (prev.length > totalTravelers) {
        return prev.slice(0, totalTravelers);
      }
      return prev;
    });
  }, [form.adults, form.kids]);

  // Fetch geo-based pricing on page load
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch(`${API_URL}/api/pricing`);
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.log('Using default pricing');
      }
      setPricingLoaded(true);
    };
    fetchPricing();
  }, []);

  // Fetch travel quotes from backend
  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const res = await fetch(`${API_URL}/api/travel-quotes`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.length > 0) {
            setTravelQuotes(data);
          }
        }
      } catch (err) {
        console.log('Using default quotes');
      }
    };
    fetchQuotes();
  }, []);

  // Show spin wheel popup on page load for non-logged-in users
  useEffect(() => {
    // Don't show if already processing an order or not on form stage
    if (orderId || stage !== 'form') return;
    
    // Don't show if user is logged in (has an account)
    if (customer) return;
    
    // Don't show if already showing
    if (spinWheelShownRef.current) return;
    
    // Show popup for non-logged-in users every time they visit the page
    const timer = setTimeout(() => {
      setShowSpinWheel(true);
      spinWheelShownRef.current = true;
    }, 1500);
    return () => clearTimeout(timer);
  }, [orderId, stage, customer]);
  
  // Auto-apply discount code if user has one from spin wheel
  useEffect(() => {
    const wonCode = localStorage.getItem('spinWheelWonCode');
    if (wonCode && !discountCode) {
      setDiscountCode(wonCode);
      // Auto-validate the code after setting
      setTimeout(() => validateDiscount(), 500);
    }
  }, [discountCode]);

  // Validate discount code
  const validateDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountValidation(null);
      return;
    }
    setValidatingDiscount(true);
    try {
      const res = await fetch(`${API_URL}/api/validate-discount-code?code=${encodeURIComponent(discountCode)}&original_price=${pricing.price}&currency=${pricing.currency}`, {
        method: 'POST',
      });
      const data = await res.json();
      setDiscountValidation(data);
      if (data.valid) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch {
      toast.error('Could not validate discount code');
      setDiscountValidation(null);
    }
    setValidatingDiscount(false);
  };

  // After returning from payment with an order_id, poll status
  const pollStatus = useCallback(async () => {
    pollCount.current += 1;
    // Allow up to 15 minutes (300 polls × 3 seconds) for complex AI generation
    if (pollCount.current > 300) {
      clearInterval(pollRef.current);
      // Do one final check before showing timeout message
      try {
        const finalRes = await fetch(`${API_URL}/api/itinerary/status/${orderId}`, { headers: auth() });
        if (finalRes.ok) {
          const finalData = await finalRes.json();
          if (finalData.itinerary) {
            setItinerary(finalData.itinerary);
            setStage('reveal');
            loadPast();
            return;
          }
        }
      } catch { /* ignore */ }
      setErrorMsg(`Your itinerary is still being crafted. Your payment is safe — you can check "My Itineraries" in your account, keep waiting, or contact our team with reference ${orderId}.`);
      setStage('failed');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/itinerary/status/${orderId}`, { headers: auth() });
      if (!res.ok) return;
      const data = await res.json();
      if (data.itinerary) {
        setItinerary(data.itinerary);
        setStage('reveal'); // Show magic reveal first
        clearInterval(pollRef.current);
        loadPast();
      } else if (data.stage === 'failed') {
        clearInterval(pollRef.current);
        setErrorMsg(data.error || 'Generation failed. Your payment is safe — please contact our team.');
        setStage('failed');
      } else if (data.payment_status === 'paid') {
        setStage('generating');
        if (Array.isArray(data.destinations) && data.destinations.length) {
          setGenDestinations(data.destinations);
        }
        // Anchor to the backend's real start time so a refresh cannot restart the countdown
        if (!generationStartTimeRef.current) {
          const backendStart = data.gen_started_at ? Date.parse(data.gen_started_at) : NaN;
          const start = Number.isNaN(backendStart) ? Date.now() : backendStart;
          generationStartTimeRef.current = start;
          setGenerationStartTime(start);
        }
      } else {
        setStage('processing');
      }
    } catch { /* ignore */ }
  }, [orderId, loadPast]);

  useEffect(() => {
    if (orderId && customer) {
      setStage('processing');
      pollStatus();
      pollRef.current = setInterval(pollStatus, 3000);
      return () => clearInterval(pollRef.current);
    }
    return undefined;
  }, [orderId, customer, pollStatus]);

  const doCheckout = async () => {
    setSubmitting(true);
    try {
      const budget = `${form.budgetLevel}${form.budgetAmount ? ` (~₹${form.budgetAmount})` : ''}`;
      
      // Check if it's international trip and prepare nationality data
      const isIntl = isInternationalTrip(form.origin, form.destinations);
      const nationalities = isIntl ? travelerNationalities.map((code, idx) => {
        const nat = NATIONALITIES.find(n => n.code === code);
        return {
          traveler: idx < Number(form.adults) ? `Adult ${idx + 1}` : `Child ${idx - Number(form.adults) + 1}`,
          nationality_code: code,
          nationality_name: nat?.name || '',
          country: nat?.country || ''
        };
      }).filter(n => n.nationality_code) : [];
      
      // Warn user if international trip but no nationalities selected
      if (isIntl && nationalities.length === 0) {
        const proceed = window.confirm(
          'You haven\'t selected nationalities for travelers. Visa requirements won\'t be included in your itinerary.\n\nDo you want to continue anyway?'
        );
        if (!proceed) {
          setSubmitting(false);
          // Scroll to visa section
          document.querySelector('[data-testid="visa-requirements-section"]')?.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }
      
      const payload = {
        origin_url: window.location.origin,
        form: {
          origin: form.origin, 
          destinations: form.destinations, 
          trip_type: form.trip_type,
          transport: form.transport, 
          hotel_preference: (form.hotel_preference || []).join(', ') || 'Any',
          adults: Number(form.adults), 
          kids: Number(form.kids),
          budget, 
          days: Number(form.days),
          start_date: form.startDate,
          special_notes: form.specialNotes,
          is_international: isIntl,
          traveler_nationalities: nationalities,
          want_hotel_recommendations: wantHotelRecommendations,
          custom_hotels: customHotels.length > 0 ? customHotels : null,
          // Pre-booked flight details (optional, empty strings when not provided)
          flight_booked: !!form.flightBooked,
          outbound_flight_no: form.flightBooked ? form.outboundFlightNo : '',
          outbound_departure: form.flightBooked ? form.outboundDeparture : '',
          outbound_arrival: form.flightBooked ? form.outboundArrival : '',
          return_flight_no: form.flightBooked ? form.returnFlightNo : '',
          return_departure: form.flightBooked ? form.returnDeparture : '',
          return_arrival: form.flightBooked ? form.returnArrival : '',
          // Language (user-picked) & currency (geo-detected from IP)
          output_language: (typeof window !== 'undefined' && localStorage.getItem('navkar_language')) || 'en',
          display_currency: pricing.currency || 'INR',
        },
        discount_code: discountValidation?.valid ? discountCode : null,
        currency: pricing.currency,
        country_code: pricing.country_code,
      };
      const res = await fetch(`${API_URL}/api/itinerary/checkout`, {
        method: 'POST', 
        headers: { ...auth(), 'Content-Type': 'application/json' }, 
        body: JSON.stringify(payload),
      });
      
      const data = await res.json();
      console.log('Checkout response:', data);
      
      if (res.ok && data.bypassed) {
        // Redirect to generation page
        const redirectUrl = `${window.location.origin}/instant-itinerary?order_id=${data.order_id}`;
        console.log('Redirecting to:', redirectUrl);
        window.location.href = redirectUrl;
        return;
      }
      
      if (res.ok && data.razorpay) {
        const loaded = await new Promise((resolve) => {
          if (window.Razorpay) return resolve(true);
          const s = document.createElement('script');
          s.src = 'https://checkout.razorpay.com/v1/checkout.js';
          s.onload = () => resolve(true);
          s.onerror = () => resolve(false);
          document.body.appendChild(s);
        });
        if (!loaded) {
          toast.error('Could not load payment window. Please check your connection and try again.');
          return;
        }
        const rzp = new window.Razorpay({
          key: data.razorpay.key_id,
          order_id: data.razorpay.order_id,
          amount: data.razorpay.amount,
          currency: data.razorpay.currency,
          name: data.razorpay.name,
          description: data.razorpay.description,
          prefill: data.razorpay.prefill,
          theme: { color: '#d97706' },
          handler: async (response) => {
            try {
              const vRes = await fetch(`${API_URL}/api/payment/razorpay/verify`, {
                method: 'POST',
                headers: { ...auth(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  order_id: data.order_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              });
              if (vRes.ok) {
                window.location.href = `${window.location.origin}/instant-itinerary?order_id=${data.order_id}`;
              } else {
                const vErr = await vRes.json();
                toast.error(vErr.detail || 'Payment verification failed. Please contact support.');
              }
            } catch {
              toast.error('Payment verification failed. Please contact support with your payment ID.');
            }
          },
          modal: {
            ondismiss: () => {
              setSubmitting(false);
              toast.info('Payment cancelled');
            },
          },
        });
        rzp.on('payment.failed', (resp) => {
          console.error('Razorpay payment failed:', resp.error);
          toast.error(resp.error?.description || 'Payment failed. Please try again.');
        });
        rzp.open();
      } else {
        // Handle FastAPI validation errors which return detail as array of objects
        let errorMessage = 'Could not start payment';
        if (data.detail) {
          if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (Array.isArray(data.detail)) {
            // FastAPI validation error format: [{type, loc, msg, input, url}, ...]
            errorMessage = data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
          } else if (typeof data.detail === 'object') {
            errorMessage = data.detail.msg || data.detail.message || JSON.stringify(data.detail);
          }
        }
        toast.error(errorMessage);
      }
    } catch (err) { 
      console.error('Checkout error:', err);
      toast.error('Connection issue. Please check your internet and try again.');
    }
    finally { setSubmitting(false); }
  };

  const startCheckout = () => {
    if (!form.origin.trim()) { toast.error('Please enter your starting city (origin)'); return; }
    if (form.destinations.length === 0) { toast.error('Add at least one destination'); return; }
    if (!customer) { setShowAuth(true); return; }
    doCheckout();
  };

  const handleAuthed = () => { setShowAuth(false); doCheckout(); };

  // Handle spin wheel close - just close, will show again next visit if not logged in
  const handleSpinWheelClose = () => {
    setShowSpinWheel(false);
  };

  // Handle spin wheel code won - auto-apply the discount
  const handleSpinWheelCodeWon = (code) => {
    if (code) {
      localStorage.setItem('spinWheelWonCode', code);
      setDiscountCode(code);
      // Auto-validate after a brief delay
      setTimeout(async () => {
        try {
          const res = await fetch(`${API_URL}/api/validate-discount-code?code=${encodeURIComponent(code)}&original_price=${pricing.price}&currency=${pricing.currency}`, {
            method: 'POST',
          });
          const data = await res.json();
          setDiscountValidation(data);
          if (data.valid) {
            toast.success(`🎉 ${data.message} - Discount auto-applied!`);
          }
        } catch {
          // Silent fail - user can manually apply later
        }
      }, 500);
    }
  };

  const sendToSupport = async (id) => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/my/itineraries/${id}/send-to-support`, { method: 'POST', headers: auth() });
      const data = await res.json();
      if (res.ok) toast.success(data.message || 'Sent to our team! We will contact you soon.');
      else {
        const errMsg = typeof data.detail === 'string' ? data.detail : 
                       Array.isArray(data.detail) ? data.detail.map(e => e.msg).join(', ') : 
                       'Failed to send';
        toast.error(errMsg);
      }
    } catch { toast.error('Connection error'); }
    finally { setSending(false); }
  };

  const handleDownload = (itinId) => {
    if (!itinId) return;
    window.open(`${API_URL}/api/itinerary-pdf/${itinId}`, '_blank', 'noopener,noreferrer');
  };

  // Download GST Invoice
  const handleDownloadInvoice = async () => {
    if (!orderId || !customer) return;
    setDownloadingInvoice(true);
    try {
      const res = await fetch(`${API_URL}/api/invoice/${orderId}`, {
        headers: auth()
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice_${orderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Invoice downloaded!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to download invoice');
      }
    } catch (err) {
      toast.error('Failed to download invoice');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  // Email Invoice to customer
  const handleEmailInvoice = async () => {
    if (!orderId || !customer) return;
    setEmailingInvoice(true);
    try {
      const res = await fetch(`${API_URL}/api/invoice/${orderId}/email`, {
        method: 'POST',
        headers: auth()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Invoice sent to your email!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send invoice');
      }
    } catch (err) {
      toast.error('Failed to send invoice');
    } finally {
      setEmailingInvoice(false);
    }
  };

  const handleRevealComplete = () => {
    setStage('preview'); // Show preview card first, then user can view full itinerary
  };

  const handleViewItinerary = () => {
    setStage('done'); // Now show the full itinerary
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <section className="min-h-screen bg-gray-50 py-12" data-testid="instant-itinerary-page">
      {/* Spin Wheel Popup - shows on page load */}
      {showSpinWheel && (
        <SpinWheelPopup 
          onClose={handleSpinWheelClose}
          onCodeWon={handleSpinWheelCodeWon}
        />
      )}

      {/* Magic reveal animation */}
      {stage === 'reveal' && <MagicRevealAnimation onComplete={handleRevealComplete} />}

      <div className="container mx-auto px-4 max-w-4xl">
        {/* Hero Header for Form Stage */}
        {stage === 'form' && (
          <div className="text-center mb-10">
            {/* AI Name Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 px-5 py-2.5 rounded-full mb-4">
              <Sparkles className="w-5 h-5 text-amber-600" />
              <span className="text-amber-700 font-bold text-base md:text-lg">Powered by {AI_NAME}</span>
            </div>
            
            {/* Main Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mt-3 leading-tight">
              Your Dream Itinerary
              <br />
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                in Just 10 Minutes
              </span>
            </h1>
            
            {/* Price Highlight */}
            <div className="mt-6 inline-flex items-center gap-3 bg-white border-2 border-amber-400 rounded-2xl px-6 py-3 shadow-lg shadow-amber-100">
              <div className="text-left">
                <p className="text-sm text-gray-500">Complete Itinerary</p>
                <p className="text-3xl font-bold text-amber-600">{pricing.formatted}</p>
                {pricing.country_code !== 'IN' && (
                  <p className="text-xs text-gray-400">{pricing.country}</p>
                )}
              </div>
              <div className="w-px h-12 bg-amber-200" />
              <div className="text-left">
                <p className="text-sm text-gray-500">Delivery Time</p>
                <p className="text-3xl font-bold text-green-600">~10 min</p>
              </div>
            </div>

            {/* Language switcher moved below video — see block after the intro video */}

            {/* NavMind Intro Video (YouTube) — click-to-play with independent EN/HI toggle */}
            <div className="mt-8 max-w-2xl mx-auto" data-testid="navmind-video-section">
              <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-semibold">Watch NavMind in 60 seconds</span>
                  </div>
                  <div className="flex rounded-full bg-white/10 p-1 text-sm font-semibold" role="tablist" aria-label="Video language">
                    <button
                      onClick={() => setVideoLangManually('en')}
                      className={`px-4 py-1.5 rounded-full transition-colors ${videoLang === 'en' ? 'bg-white text-slate-900 shadow' : 'text-white/85 hover:text-white'}`}
                      data-testid="video-lang-en"
                      aria-pressed={videoLang === 'en'}
                    >
                      English
                    </button>
                    <button
                      onClick={() => setVideoLangManually('hi')}
                      className={`px-4 py-1.5 rounded-full transition-colors ${videoLang === 'hi' ? 'bg-white text-slate-900 shadow' : 'text-white/85 hover:text-white'}`}
                      data-testid="video-lang-hi"
                      aria-pressed={videoLang === 'hi'}
                    >
                      हिन्दी
                    </button>
                  </div>
                </div>
                <div className="relative aspect-video bg-black">
                  {videoPlaying ? (
                    <iframe
                      key={videoLang}
                      className="absolute inset-0 w-full h-full"
                      src={`https://www.youtube-nocookie.com/embed/${videoLang === 'hi' ? 'uqXm7xi51z0' : 'kq6JCHa21SY'}?autoplay=1&rel=0&modestbranding=1&iv_load_policy=3&playlist=${videoLang === 'hi' ? 'uqXm7xi51z0' : 'kq6JCHa21SY'}&hl=${videoLang}`}
                      title={`NavMind intro (${videoLang === 'hi' ? 'Hindi' : 'English'})`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid="navmind-video-iframe"
                    />
                  ) : (
                    <button
                      onClick={() => setVideoPlaying(true)}
                      className="group absolute inset-0 w-full h-full cursor-pointer"
                      data-testid="navmind-video-play-btn"
                      aria-label="Play NavMind intro video"
                    >
                      <img
                        key={`thumb-${videoLang}`}
                        src={`https://img.youtube.com/vi/${videoLang === 'hi' ? 'uqXm7xi51z0' : 'kq6JCHa21SY'}/hqdefault.jpg`}
                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `https://img.youtube.com/vi/${videoLang === 'hi' ? 'uqXm7xi51z0' : 'kq6JCHa21SY'}/0.jpg`; }}
                        alt="NavMind intro thumbnail"
                        className="w-full h-full object-cover"
                        loading="eager"
                      />
                      <div className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <div className="w-20 h-20 rounded-full bg-white/95 group-hover:bg-white group-hover:scale-110 transition-all flex items-center justify-center shadow-2xl">
                          <div className="w-0 h-0 border-t-[14px] border-t-transparent border-b-[14px] border-b-transparent border-l-[22px] border-l-slate-900 ml-1.5" />
                        </div>
                      </div>
                      <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white text-xs">
                        <span className="bg-black/50 backdrop-blur px-2 py-1 rounded">
                          {videoLang === 'hi' ? 'हिन्दी में देखें' : 'Watch in English'}
                        </span>
                        <span className="bg-red-600 px-2 py-1 rounded font-semibold">YouTube</span>
                      </div>
                    </button>
                  )}
                </div>
                <div className="px-4 py-2 text-center text-xs text-gray-500">
                  See how NavMind turns your inputs into a designer PDF itinerary
                </div>
              </div>
            </div>

            {/* Language Switcher (moved below video, above See Sample) */}
            <div className="mt-6 flex flex-col items-center gap-2" data-testid="ii-lang-switcher-wrap">
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Generate in your language</p>
              <div className="flex justify-center">
                <LanguageSwitcher />
              </div>
            </div>

            {/* See Sample Button (translated label, content stays the same) */}
            <div className="mt-4">
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button
                  onClick={() => setShowSamplePreview(true)}
                  variant="outline"
                  className="border-2 border-amber-400 text-amber-700 hover:bg-amber-50 px-6 py-3 text-base font-semibold"
                  data-testid="see-sample-itinerary-btn"
                >
                  <Eye className="w-5 h-5 mr-2" /> {t('sample.seeSampleBtn', 'See Sample Itinerary')}
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-2 text-center">See what you&apos;ll get before you pay</p>
            </div>

            {/* Why NavMind vs generic AI comparison */}
            <div className="mt-8 max-w-2xl mx-auto text-left" data-testid="why-navmind-section">
              <div className="bg-white border border-amber-200 rounded-2xl shadow-lg shadow-amber-50 overflow-hidden">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-3">
                  <p className="text-white font-bold text-base md:text-lg text-center">
                    {t('whyNav.title')}
                  </p>
                </div>
                <div className="divide-y divide-gray-100">
                  <div className="grid grid-cols-[1fr,72px,72px] sm:grid-cols-[1fr,110px,110px] items-center px-4 py-2 bg-amber-50/60 text-xs sm:text-sm font-semibold text-gray-600">
                    <span />
                    <span className="text-center text-amber-700">{t('whyNav.colUs')}</span>
                    <span className="text-center text-gray-400">{t('whyNav.colThem')}</span>
                  </div>
                  {/* Hero selling point */}
                  <div className="grid grid-cols-[1fr,72px,72px] sm:grid-cols-[1fr,110px,110px] items-center px-4 py-3 bg-gradient-to-r from-amber-100/80 to-orange-50 border-l-4 border-amber-500" data-testid="why-navmind-hero-row">
                    <span className="text-sm sm:text-base font-bold text-gray-900 pr-2">
                      {t('whyNav.heroLeft')}
                    </span>
                    <span className="flex justify-center"><CheckCircle className="w-6 h-6 text-green-600" /></span>
                    <span className="flex justify-center text-center">
                      <span className="text-[10px] sm:text-xs text-gray-400 leading-tight">{t('whyNav.heroRight')}</span>
                    </span>
                  </div>
                  {(t('whyNav.rows', { returnObjects: true }) || []).map((row, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr,72px,72px] sm:grid-cols-[1fr,110px,110px] items-center px-4 py-2.5">
                      <span className="text-xs sm:text-sm text-gray-700 pr-2">{row.f}</span>
                      <span className="flex justify-center"><CheckCircle className="w-5 h-5 text-green-500" /></span>
                      <span className="flex justify-center text-center">
                        {(!row.them || row.them === '' || row.them === false)
                          ? <X className="w-5 h-5 text-red-400" />
                          : <span className="text-[10px] sm:text-xs text-gray-400 leading-tight">{row.them}</span>}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="bg-amber-50 px-5 py-3 text-center">
                  <p className="text-xs sm:text-sm text-gray-700">
                    <span className="font-semibold text-amber-700">
                      {t('whyNav.footer', { price: pricing.formatted })}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            
            {/* Value Proposition */}
            <div className="mt-6 max-w-2xl mx-auto">
              <p className="text-lg text-gray-600">
                Why wait <span className="font-semibold text-gray-800">days for travel agents</span> when 
                <span className="text-amber-600 font-semibold"> {AI_NAME}</span> creates your 
                <span className="font-semibold"> personalized, detailed itinerary</span> in minutes?
              </p>
            </div>
            
            {/* Features */}
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {[
                { icon: CheckCircle, text: 'Day-by-day plan' },
                { icon: Hotel, text: 'Hotel picks' },
                { icon: Plane, text: 'Flight options' },
                { icon: Bus, text: 'Internal transfers' },
                { icon: FileCheck, text: 'Visa requirements' },
                { icon: Utensils, text: 'Restaurant recs' },
                { icon: Wallet, text: 'Cost breakdown' },
              ].map(({ icon: Icon, text }) => (
                <span key={text} className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-700 text-sm px-3 py-1.5 rounded-full">
                  <Icon className="w-4 h-4 text-amber-500" /> {text}
                </span>
              ))}
            </div>
            
            {/* See Sample Button + Language Sample Toggle moved above Why NavMind — this slot intentionally empty */}
          </div>
        )}

        {/* Sample Itinerary Preview Modal */}
        <SampleItineraryModal 
          isOpen={showSamplePreview} 
          onClose={() => setShowSamplePreview(false)} 
        />

        {/* Generating screen — photo-led, honest countdown that holds at 99% past the estimate */}
        {(stage === 'processing' || stage === 'generating') && (
          <GeneratingScreen
            startTime={generationStartTime || Date.now()}
            quotes={travelQuotes && travelQuotes.length ? travelQuotes : DEFAULT_TRAVEL_QUOTES}
            destinations={genDestinations.length ? genDestinations : (form.destinations || [])}
          />
        )}

        {stage === 'failed' && (
          <Card><CardContent className="p-10 text-center">
            <Clock className="w-10 h-10 mx-auto text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">Your itinerary is taking longer than usual</h3>
            <p className="text-gray-600 mt-2 max-w-md mx-auto">{errorMsg}</p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4 max-w-md mx-auto">
              <p className="text-green-800 text-sm">
                <strong>Don&apos;t worry!</strong> Your payment is safe and your itinerary is likely still being generated. 
                Complex trips with multiple destinations can take up to 15 minutes.
              </p>
            </div>
            <div className="flex gap-3 justify-center mt-5 flex-wrap">
              <Button 
                variant="outline" 
                onClick={() => {
                  pollCount.current = 0;
                  setStage('generating');
                  setErrorMsg('');
                  pollRef.current = setInterval(pollStatus, 3000);
                }}
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
              >
                <Clock className="w-4 h-4 mr-2" /> Keep Waiting
              </Button>
              <Button variant="outline" onClick={() => navigate('/my-itineraries')}>
                Check My Itineraries
              </Button>
              <Button variant="outline" onClick={() => navigate('/contact')}>Contact Support</Button>
            </div>
          </CardContent></Card>
        )}

        {stage === 'done' && itinerary && (
          <div>
            <ItineraryView 
              itin={itinerary} 
              onSendSupport={() => sendToSupport(itinerary.id)} 
              onDownload={() => handleDownload(itinerary.id)}
              sending={sending}
              onDownloadInvoice={orderId && customer ? handleDownloadInvoice : null}
              onEmailInvoice={orderId && customer ? handleEmailInvoice : null}
              downloadingInvoice={downloadingInvoice}
              emailingInvoice={emailingInvoice}
            />
            <div className="mt-8 text-center">
              <Button variant="outline" onClick={() => { setParams({}); setStage('form'); setItinerary(null); }}>
                <Plus className="w-4 h-4 mr-2" /> Create another itinerary
              </Button>
            </div>
          </div>
        )}

        {/* Preview stage - show summary card before full view */}
        {stage === 'preview' && itinerary && (
          <Card className="overflow-hidden">
            {/* Cover image or gradient */}
            <div className="relative h-48 bg-gradient-to-r from-amber-500 to-orange-500">
              {itinerary.content?.cover_image_url && (
                <img 
                  src={`${API_URL}${itinerary.content.cover_image_url}`} 
                  alt="Trip cover" 
                  className="w-full h-full object-cover absolute inset-0"
                />
              )}
              <div className="absolute inset-0 bg-black/30" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="flex items-center gap-2 text-amber-200 text-sm mb-1">
                  <Sparkles className="w-4 h-4" /> AI-Generated Itinerary
                </div>
                <h2 className="text-2xl font-bold">{itinerary.content?.title || itinerary.title}</h2>
              </div>
            </div>
            
            <CardContent className="p-6 space-y-6">
              {/* Quick stats */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-amber-50 rounded-lg p-3">
                  <CalendarDays className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                  <div className="text-lg font-bold text-gray-900">{itinerary.form?.days || itinerary.content?.days?.length || '—'}</div>
                  <div className="text-xs text-gray-500">Days</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <MapPinned className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                  <div className="text-lg font-bold text-gray-900">{itinerary.form?.destinations?.length || '—'}</div>
                  <div className="text-xs text-gray-500">Destinations</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <Users className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                  <div className="text-lg font-bold text-gray-900">{(itinerary.form?.adults || 0) + (itinerary.form?.kids || 0)}</div>
                  <div className="text-xs text-gray-500">Travellers</div>
                </div>
              </div>

              {/* Overview */}
              {itinerary.content?.overview && (
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed">{itinerary.content.overview}</p>
                </div>
              )}

              {/* What's included */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Your itinerary includes:</h4>
                <ul className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Day-by-day plan</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Hotel recommendations</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Flight options</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Internal transfers</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Visa requirements</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Restaurant picks</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Cost estimates</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Travel tips</li>
                </ul>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={handleViewItinerary}
                  data-testid="view-full-itinerary-btn"
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg"
                >
                  <Globe className="w-5 h-5 mr-2" /> View Full Itinerary
                </Button>
                {itinerary.pdf_url && (
                  <Button 
                    onClick={() => handleDownload(itinerary.id)}
                    variant="outline"
                    data-testid="download-pdf-preview-btn"
                    className="flex-1 py-6 text-lg"
                  >
                    <Download className="w-5 h-5 mr-2" /> Download PDF
                  </Button>
                )}
              </div>

              <p className="text-xs text-center text-gray-400">
                View the full itinerary to see all details, then download or send to our team to book.
              </p>
            </CardContent>
          </Card>
        )}

        {stage === 'form' && (
          <Card><CardContent className="p-6 space-y-5">
            {/* Origin with autocomplete */}
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-1"><MapPin className="w-4 h-4 text-amber-600" /> {t('ii.origin')}</label>
              <DestinationAutocomplete
                value={originInput}
                onChange={(v) => { setOriginInput(v); setField('origin', v); }}
                onSelect={selectOrigin}
                placeholder={t('ii.originPh')}
                keepValueOnSelect={true}
              />
            </div>

            {/* Destinations with autocomplete */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('ii.destinations')}</label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <DestinationAutocomplete
                    value={destInput}
                    onChange={setDestInput}
                    onSelect={addDest}
                    placeholder="Type to search destinations..."
                  />
                </div>
                <Button type="button" onClick={() => addDest()} data-testid="add-destination-btn" variant="outline"><Plus className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {form.destinations.map((d, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 rounded-full px-3 py-1 text-sm">
                    <MapPin className="w-3 h-3" /> {d} <button onClick={() => removeDest(i)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('ii.tripType')}</label>
                <select data-testid="trip-type-select" value={form.trip_type} onChange={(e) => setField('trip_type', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                  {TRIP_TYPES.map((tp) => <option key={tp} value={tp}>{t(`ii.tripTypes.${tp}`, tp)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1"><Plane className="w-4 h-4 text-amber-600" /> {t('ii.transport')}</label>
                <select data-testid="transport-select" value={form.transport} onChange={(e) => setField('transport', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                  {TRANSPORT.map((tp) => <option key={tp} value={tp}>{t(`ii.transports.${tp}`, tp)}</option>)}
                </select>
              </div>
            </div>

            {/* Pre-booked Flight Details (optional, shown when transport = Flight or Mix) */}
            {(form.transport === 'Flight' || form.transport === 'Mix of options') && (
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 space-y-3" data-testid="prebooked-flight-section">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.flightBooked}
                    onChange={(e) => setField('flightBooked', e.target.checked)}
                    className="w-4 h-4 accent-sky-600"
                    data-testid="flight-booked-checkbox"
                  />
                  <span className="text-sm font-medium text-gray-800">
                    <Plane className="w-4 h-4 inline mr-1 text-sky-600" />
                    I&apos;ve already booked my flights (helps NavMind plan realistic arrival &amp; recovery days)
                  </span>
                </label>

                {form.flightBooked && (
                  <div className="space-y-4 pt-2">
                    {/* Outbound */}
                    <div>
                      <p className="text-xs font-semibold text-sky-800 uppercase tracking-wide mb-2">Outbound flight (to destination)</p>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Flight number</label>
                          <input
                            type="text"
                            placeholder="e.g. 6E-1055"
                            value={form.outboundFlightNo}
                            onChange={(e) => setField('outboundFlightNo', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            data-testid="outbound-flight-no"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Departs (origin)</label>
                          <input
                            type="datetime-local"
                            value={form.outboundDeparture}
                            onChange={(e) => setField('outboundDeparture', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            data-testid="outbound-departure"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Arrives (destination)</label>
                          <input
                            type="datetime-local"
                            value={form.outboundArrival}
                            onChange={(e) => setField('outboundArrival', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            data-testid="outbound-arrival"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Return */}
                    <div>
                      <p className="text-xs font-semibold text-sky-800 uppercase tracking-wide mb-2">Return flight (back home)</p>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Flight number</label>
                          <input
                            type="text"
                            placeholder="e.g. AI-306"
                            value={form.returnFlightNo}
                            onChange={(e) => setField('returnFlightNo', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            data-testid="return-flight-no"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Departs (destination)</label>
                          <input
                            type="datetime-local"
                            value={form.returnDeparture}
                            onChange={(e) => setField('returnDeparture', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            data-testid="return-departure"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Arrives (home)</label>
                          <input
                            type="datetime-local"
                            value={form.returnArrival}
                            onChange={(e) => setField('returnArrival', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            data-testid="return-arrival"
                          />
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-sky-700 italic">
                      NavMind will schedule light activities around your actual arrival time (e.g. late-night landing = rest, red-eye = recovery morning) and pack the final day to end before your return flight.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">
                Hotel preference <span className="text-gray-500 font-normal">— pick as many as you like</span>
              </label>
              <div className="flex flex-wrap gap-2" data-testid="hotel-pref-group">
                {HOTEL_PREFS.map((h) => {
                  const selected = (form.hotel_preference || []).includes(h);
                  return (
                    <button
                      key={h}
                      type="button"
                      data-testid={`hotel-pref-${h.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      aria-pressed={selected}
                      onClick={() => toggleHotelPref(h)}
                      className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${
                        selected
                          ? 'bg-amber-600 text-white border-amber-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-amber-400 hover:text-amber-700'
                      }`}
                    >
                      {h}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                {(form.hotel_preference || []).length > 1
                  ? `We'll suggest options in each of the ${form.hotel_preference.length} categories for every city.`
                  : 'Select more than one to compare categories side by side.'}
              </p>
            </div>

            {/* Hotel/Villa Recommendations Section */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Hotel className="w-4 h-4 text-amber-600" />
                  Want hotel/villa recommendations?
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setWantHotelRecommendations(true)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      wantHotelRecommendations 
                        ? 'bg-amber-600 text-white' 
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setWantHotelRecommendations(false)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      !wantHotelRecommendations 
                        ? 'bg-gray-600 text-white' 
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
              
              {/* Custom Hotels/Villas Input */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Already have hotels/villas in mind? Add them here:
                </label>
                <div className="space-y-2">
                  {/* Input row for new hotel */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      type="text"
                      placeholder="Hotel/Villa name"
                      value={newHotel.name}
                      onChange={(e) => setNewHotel({ ...newHotel, name: e.target.value })}
                      className="flex-1"
                    />
                    <Input
                      type="date"
                      placeholder="Check-in"
                      value={newHotel.checkIn}
                      onChange={(e) => setNewHotel({ ...newHotel, checkIn: e.target.value })}
                      min={form.startDate || new Date().toISOString().split('T')[0]}
                      className="w-full sm:w-32"
                    />
                    <Input
                      type="date"
                      placeholder="Check-out"
                      value={newHotel.checkOut}
                      onChange={(e) => setNewHotel({ ...newHotel, checkOut: e.target.value })}
                      min={newHotel.checkIn || form.startDate || new Date().toISOString().split('T')[0]}
                      className="w-full sm:w-32"
                    />
                    <Button 
                      type="button" 
                      onClick={addCustomHotel} 
                      variant="outline"
                      disabled={!newHotel.name.trim()}
                      className="shrink-0"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* List of added hotels */}
                  {customHotels.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {customHotels.map((hotel, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-2">
                          <Hotel className="w-4 h-4 text-amber-600 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-900">{hotel.name}</span>
                            {(hotel.checkIn || hotel.checkOut) && (
                              <span className="text-xs text-gray-500 ml-2">
                                {hotel.checkIn && new Date(hotel.checkIn).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                {hotel.checkIn && hotel.checkOut && ' - '}
                                {hotel.checkOut && new Date(hotel.checkOut).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </div>
                          <button 
                            type="button"
                            onClick={() => removeCustomHotel(idx)} 
                            className="text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  These will be included in your itinerary instead of AI recommendations for those dates
                </p>
              </div>
            </div>

            {/* Adults & Kids row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1"><Users className="w-4 h-4 text-amber-600" /> Adults</label>
                <Input type="number" min="1" data-testid="adults-input" value={form.adults} onChange={(e) => setField('adults', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Kids</label>
                <Input type="number" min="0" data-testid="kids-input" value={form.kids} onChange={(e) => setField('kids', e.target.value)} />
              </div>
            </div>
            
            {/* Start Date & Days row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1"><CalendarDays className="w-4 h-4 text-amber-600" /> Start Date</label>
                <Input 
                  type="date" 
                  data-testid="start-date-input" 
                  value={form.startDate} 
                  onChange={(e) => setField('startDate', e.target.value)} 
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1"><Clock className="w-4 h-4 text-amber-600" /> Days</label>
                <Input type="number" min="1" max="30" data-testid="days-input" value={form.days} onChange={(e) => setField('days', e.target.value)} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Budget level</label>
                <select data-testid="budget-level-select" value={form.budgetLevel} onChange={(e) => setField('budgetLevel', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                  {BUDGET_LEVELS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1"><Wallet className="w-4 h-4 text-amber-600" /> Total budget (₹, optional)</label>
                <Input type="number" min="0" data-testid="budget-amount-input" value={form.budgetAmount} onChange={(e) => setField('budgetAmount', e.target.value)} placeholder="e.g. 150000" />
              </div>
            </div>

            {/* Special Notes Field */}
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                <MessageSquare className="w-4 h-4 text-amber-600" /> Special Requests (optional)
              </label>
              <textarea
                data-testid="special-notes-input"
                value={form.specialNotes}
                onChange={(e) => setField('specialNotes', e.target.value)}
                placeholder="Any specific activities, landmarks, restaurants, or experiences you'd like us to include? E.g., 'Must visit Eiffel Tower at sunset', 'Include a cooking class', 'Avoid crowded tourist spots'..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[80px] resize-y"
                rows={3}
              />
              <p className="text-xs text-gray-400 mt-1">Tell us about must-see places, preferred activities, dietary needs, or anything special you&apos;d like in your trip</p>
            </div>

            {/* Visa Requirements Section - Only show for international trips */}
            {isInternationalTrip(form.origin, form.destinations) && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200" data-testid="visa-requirements-section">
                <div className="flex items-center gap-2 mb-3">
                  <FileCheck className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">Visa Requirements</h4>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">International Trip</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Please select nationality for each traveler so we can include accurate visa requirements in your itinerary.
                </p>
                
                <div className="space-y-3">
                  {travelerNationalities.map((nationality, idx) => {
                    const isAdult = idx < Number(form.adults || 1);
                    const travelerLabel = isAdult 
                      ? `Adult ${idx + 1}` 
                      : `Child ${idx - Number(form.adults || 1) + 1}`;
                    
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <label className="text-sm font-medium text-gray-700 w-20 shrink-0">
                          {travelerLabel}
                        </label>
                        <select
                          data-testid={`nationality-select-${idx}`}
                          value={nationality}
                          onChange={(e) => {
                            const newNationalities = [...travelerNationalities];
                            newNationalities[idx] = e.target.value;
                            setTravelerNationalities(newNationalities);
                          }}
                          className={`flex-1 border rounded-md px-3 py-2 text-sm bg-white ${
                            nationality ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'
                          }`}
                        >
                          <option value="">⚠️ Select nationality...</option>
                          {NATIONALITIES.map(n => (
                            <option key={n.code} value={n.code}>{n.name} ({n.country})</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
                
                <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  Visa information will be customized based on each traveler&apos;s nationality
                </p>
              </div>
            )}

            {/* Discount Code */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <label className="block text-sm font-medium mb-2 flex items-center gap-1">
                🎟️ Have a discount code?
              </label>
              <div className="flex gap-2">
                <Input
                  data-testid="discount-code-input"
                  value={discountCode}
                  onChange={(e) => {
                    setDiscountCode(e.target.value.toUpperCase());
                    setDiscountValidation(null);
                  }}
                  placeholder="Enter code (e.g., WELCOME20)"
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={validateDiscount}
                  disabled={validatingDiscount || !discountCode.trim()}
                  data-testid="apply-discount-btn"
                >
                  {validatingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                </Button>
              </div>
              {discountValidation && (
                <div className={`mt-2 text-sm ${discountValidation.valid ? 'text-green-600' : 'text-red-500'}`}>
                  {discountValidation.valid ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>{discountValidation.message} — You save ₹{discountValidation.discount_amount}!</span>
                    </div>
                  ) : (
                    <span>{discountValidation.message}</span>
                  )}
                </div>
              )}
            </div>

            {/* Price Display */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Itinerary Price</span>
                {discountValidation?.valid ? (
                  <div className="text-right">
                    <span className="text-gray-400 line-through text-sm mr-2">{pricing.formatted}</span>
                    <span className="text-lg font-bold text-gray-700">{pricing.symbol}{discountValidation.final_price}</span>
                  </div>
                ) : (
                  <span className="text-lg font-bold text-gray-700">{pricing.formatted}</span>
                )}
              </div>
              {discountValidation?.valid && (
                <div className="text-green-600 text-sm text-right mt-1">
                  You save {pricing.symbol}{discountValidation.discount_amount}!
                </div>
              )}
              
              {/* GST Display - Only for INR */}
              {pricing.currency === 'INR' && (
                <>
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-amber-200">
                    <span className="text-sm text-gray-600">GST @ 18%</span>
                    <span className="text-sm text-gray-600">
                      {pricing.symbol}{(discountValidation?.valid 
                        ? (discountValidation.final_price * 0.18).toFixed(2) 
                        : (pricing.price * 0.18).toFixed(2))}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2 pt-2 border-t-2 border-amber-300">
                    <span className="font-semibold text-gray-800">Total (incl. GST)</span>
                    <span className="text-2xl font-bold text-amber-600">
                      {pricing.symbol}{(discountValidation?.valid 
                        ? (discountValidation.final_price * 1.18).toFixed(0) 
                        : (pricing.price * 1.18).toFixed(0))}
                    </span>
                  </div>
                </>
              )}
              {pricing.currency !== 'INR' && (
                <div className="flex justify-between items-center mt-2 pt-2 border-t-2 border-amber-300">
                  <span className="font-semibold text-gray-800">Total</span>
                  <span className="text-2xl font-bold text-amber-600">
                    {pricing.symbol}{discountValidation?.valid ? discountValidation.final_price : pricing.price}
                  </span>
                </div>
              )}
            </div>

            <label className="flex items-start gap-2.5 mb-3 cursor-pointer select-none" data-testid="terms-agree-label">
              <input
                type="checkbox"
                checked={agreedTerms}
                onChange={(e) => setAgreedTerms(e.target.checked)}
                data-testid="terms-agree-checkbox"
                className="mt-0.5 w-4 h-4 accent-amber-600 shrink-0"
              />
              <span className="text-sm text-gray-600">
                I have read and agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-amber-700 font-medium underline hover:text-amber-800">
                  Terms &amp; Conditions
                </a>
              </span>
            </label>

            <Button onClick={startCheckout} disabled={submitting || !agreedTerms} data-testid="pay-generate-btn" className="w-full bg-amber-600 hover:bg-amber-700 text-white text-lg py-6 disabled:opacity-50">
              <Sparkles className="w-5 h-5 mr-2" /> {submitting ? 'Starting…' : `Pay & Generate · ${pricing.symbol}${pricing.currency === 'INR' ? ((discountValidation?.valid ? discountValidation.final_price * 1.18 : pricing.price * 1.18)).toFixed(0) : (discountValidation?.valid ? discountValidation.final_price : pricing.price)}`}
            </Button>
            {!agreedTerms && (
              <p className="text-xs text-amber-600 text-center" data-testid="terms-agree-hint">Please accept the Terms &amp; Conditions to continue.</p>
            )}
            <p className="text-xs text-gray-400 text-center">
              Detailed itinerary with photos &amp; hotel suggestions, generated instantly after payment and saved to your account.
              {!customer && " You'll create a quick account at checkout."}
              {pricing.currency === 'INR' && <span className="block mt-1">GST Invoice will be available for download after payment.</span>}
            </p>
          </CardContent></Card>
        )}

        {/* Login modal */}
        {showAuth && (
          <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowAuth(false)}>
            <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <Sparkles className="w-10 h-10 mx-auto text-amber-500 mb-2" />
                  <h3 className="text-lg font-bold text-gray-900">Almost there!</h3>
                  <p className="text-sm text-gray-600">Create a free account (or log in) to get your {pricing.formatted} itinerary. Your trip details are saved.</p>
                </div>
                <InlineAuth onSuccess={handleAuthed} actionLabel="Continue to Payment" />
                <button onClick={() => setShowAuth(false)} className="block mx-auto mt-3 text-xs text-gray-400 hover:text-gray-600">Cancel</button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Past itineraries moved to My Account */}
        {customer && stage === 'form' && (
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/my-account')}
              data-testid="view-past-itineraries-link"
              className="text-sm text-amber-700 hover:text-amber-800 inline-flex items-center gap-1.5 font-medium"
            >
              <FileText className="w-4 h-4" /> View your past itineraries &amp; invoices in My Account →
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default InstantItineraryPage;
